import "./App.css";

const integrations = [
  "Rekor",
  "Axon",
  "Axis ALPV",
  "Hikvision",
  "Motorola",
  "Vaxtor",
];

const features = [
  {
    number: "01",
    title: "Federated Search",
    text: "Search authorized detections across participating systems through one operational interface.",
  },
  {
    number: "02",
    title: "Agency-Controlled Sharing",
    text: "Each organization determines which partners can access its data and how that access is governed.",
  },
  {
    number: "03",
    title: "Real-Time Alerts",
    text: "Turn agency-managed hotlists into actionable web, mobile, push, and email notifications.",
  },
  {
    number: "04",
    title: "Audit & Reporting",
    text: "Record search justifications and case information, review audit logs, and produce PDF, CSV, and image-supported reports.",
  },
];

function App() {
  return (
    <div className="site">
      <header className="topbar">
        <div className="container topbar-inner">
          <a className="wordmark" href="#top" aria-label="OdinEye home">
            <span className="wordmark-main">ODINEYE</span>
            <span className="wordmark-sub">Federated LPR Platform</span>
          </a>

          <nav className="nav" aria-label="Primary navigation">
            <a href="#product">Product</a>
            <a href="#platform">Platform</a>
            <a href="#company">Company</a>
            <a className="nav-login" href="https://www.odineyelpr.com">
              Customer Login
            </a>
          </nav>
        </div>
      </header>

      <main id="top">
        <section className="hero container">
          <div className="hero-copy">
            <div className="eyebrow">Agency-owned data · Mission-first interoperability</div>
            <h1>Vehicle intelligence without surrendering control.</h1>
            <p className="lead">
              OdinEye is a deployed, vendor-agnostic license plate recognition platform for
              authorized agencies and approved partners. Search across connected systems, manage
              controlled data sharing, receive actionable alerts, and document activity through
              one secure web and mobile experience.
            </p>

            <div className="button-row">
              <a
                className="button button-solid"
                href="mailto:support@odineyelpr.com?subject=OdinEye%20Demo%20Request"
              >
                Request a Demo
              </a>
              <a className="button button-outline" href="#product">
                View the Platform
              </a>
            </div>

            <div className="trust-row" aria-label="OdinEye platform facts">
              <div>
                <strong>40+</strong>
                <span>Organizations</span>
              </div>
              <div>
                <strong>Web + Mobile</strong>
                <span>Operational access</span>
              </div>
              <div>
                <strong>Agency Controlled</strong>
                <span>Sharing and governance</span>
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-glow" />
            <img
              src="/product-search-results.png"
              alt="OdinEye desktop search results displaying synthetic vehicle detections and matching plate images"
            />
            <div className="live-badge">
              <span /> Live product interface
            </div>
          </div>
        </section>

        <section className="proof-band">
          <div className="container proof-grid">
            <span>Unified search</span>
            <span>Geospatial analysis</span>
            <span>Hotlist alerts</span>
            <span>Mobile operations</span>
            <span>Auditable exports</span>
          </div>
        </section>

        <section className="section container" id="product">
          <div className="section-heading">
            <div>
              <div className="section-label">Operational Product</div>
              <h2>See OdinEye in action.</h2>
            </div>
            <p>
              The interface below is running against OdinEye’s permanent synthetic demonstration
              environment: 500 fictional detections distributed across 10 simulated cameras.
            </p>
          </div>

          <figure className="showcase showcase-wide">
            <img
              src="/product-search-results.png"
              alt="OdinEye table view with six synthetic vehicle detections"
              loading="lazy"
            />
            <figcaption>
              <span>Unified detection search</span>
              Filter results by plate, state, make, color, body type, camera, and timeframe—then
              export authorized records with supporting imagery.
            </figcaption>
          </figure>

          <div className="showcase-grid">
            <figure className="showcase">
              <img
                src="/product-map-view.png"
                alt="OdinEye map view plotting synthetic detections over time"
                loading="lazy"
              />
              <figcaption>
                <span>Map and timeline analysis</span>
                Move from tabular results to a geographic timeline that reveals movement and
                recurring locations.
              </figcaption>
            </figure>

            <div className="workflow-copy">
              <div className="section-label">One Workflow</div>
              <h3>From detection to action.</h3>
              <ol>
                <li>
                  <strong>Search authorized sources.</strong>
                  <span>Query participating LPR systems without changing between vendor portals.</span>
                </li>
                <li>
                  <strong>Review the evidence.</strong>
                  <span>Inspect vehicle imagery, plate crops, timestamps, cameras, and locations.</span>
                </li>
                <li>
                  <strong>Act and document.</strong>
                  <span>Create controlled hotlists, receive alerts, and generate accountable reports.</span>
                </li>
              </ol>
            </div>
          </div>

          <div className="demo-disclosure">
            <strong>About the demonstration data</strong>
            <p>
              All vehicle imagery, license plates, agency names, detection records, case details,
              and alerts shown on this page are synthetic and were created solely for product
              demonstration and training. Map backgrounds may depict real geography, but no
              displayed record corresponds to an actual vehicle, person, investigation, or
              law-enforcement event.
            </p>
          </div>
        </section>

        <section className="section section-dark">
          <div className="container">
            <div className="section-heading mobile-heading">
              <div>
                <div className="section-label">Field Operations</div>
                <h2>Built for web and mobile.</h2>
              </div>
              <p>
                Investigators, analysts, and authorized field personnel can move from search results
                to detection context and hotlist alerts without losing operational detail.
              </p>
            </div>

            <div className="phone-gallery">
              <figure className="phone-card">
                <img
                  src="/mobile-results.png"
                  alt="OdinEye mobile results showing synthetic vehicle detections"
                  loading="lazy"
                />
                <figcaption>
                  <strong>Mobile results</strong>
                  Search results carry vehicle, camera, agency, and timestamp context into the field.
                </figcaption>
              </figure>

              <figure className="phone-card phone-card-featured">
                <img
                  src="/mobile-detection-detail.jpg"
                  alt="OdinEye mobile detection detail with synthetic vehicle image and mapped location"
                  loading="lazy"
                />
                <figcaption>
                  <strong>Detection detail</strong>
                  Review imagery and location context, open mapping, or add a vehicle to an
                  agency-controlled hotlist.
                </figcaption>
              </figure>

              <figure className="phone-card">
                <img
                  src="/mobile-alert-detail.png"
                  alt="OdinEye synthetic wanted-person hotlist alert for plate RAV1007"
                  loading="lazy"
                />
                <figcaption>
                  <strong>Actionable alerts</strong>
                  Receive group context, case information, the detection image, camera, timestamp,
                  and mapped location in one alert.
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="section container" id="platform">
          <div className="section-heading">
            <div>
              <div className="section-label">Platform</div>
              <h2>Operational capability with accountable control.</h2>
            </div>
            <p>
              OdinEye complements existing camera investments and vendor ecosystems while allowing
              participating organizations to maintain authority over their own information.
            </p>
          </div>

          <div className="feature-grid">
            {features.map((feature) => (
              <article className="feature-card" key={feature.title}>
                <div className="feature-number">{feature.number}</div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </article>
            ))}
          </div>

          <div className="integration-panel">
            <div>
              <div className="section-label">Connected Ecosystem</div>
              <h3>Vendor-agnostic by design.</h3>
              <p>
                Connect compatible LPR deployments and partner systems through a unified operating
                layer rather than replacing existing infrastructure.
              </p>
            </div>
            <div className="pill-row">
              {integrations.map((integration) => (
                <span className="pill" key={integration}>
                  {integration}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="section report-section">
          <div className="container report-grid">
            <div>
              <div className="section-label">Reporting & Accountability</div>
              <h2>Turn results into documented intelligence.</h2>
              <p>
                Authorized users can prepare image-supported reports and structured data exports for
                investigative, administrative, and legal workflows. Audit logging and redaction
                controls support responsible review of platform activity.
              </p>
              <a
                className="button button-solid"
                href="/sample-detection-report.pdf"
                target="_blank"
                rel="noreferrer"
              >
                View Sample Synthetic Report
              </a>
            </div>
            <a
              className="report-preview"
              href="/sample-detection-report.pdf"
              target="_blank"
              rel="noreferrer"
              aria-label="Open sample synthetic OdinEye report"
            >
              <img
                src="/sample-report-preview.png"
                alt="Preview of an OdinEye synthetic vehicle detection report"
                loading="lazy"
              />
            </a>
          </div>
        </section>

        <section className="section container" id="company">
          <div className="company-grid">
            <div className="company-copy">
              <div className="section-label">Company</div>
              <h2>Practical technology.<br />Accountable operations.</h2>
              <p>
                OdinEye is developed and operated by <strong>CellTrace LLC</strong>, a
                Connecticut limited liability company focused on practical technology for
                law-enforcement and public-safety organizations.
              </p>
            </div>

            <div className="company-copy company-details">
              <h3>Interoperability with agency control.</h3>
              <p>
                The platform brings vendor-agnostic ALPR integrations, federated searches,
                real-time alerts, and agency-controlled data sharing into one web and mobile
                workflow. Agencies retain ownership of their data and determine which
                authorized partners can access it.
              </p>
              <p>
                OdinEye supports lawful investigative workflows through search justifications,
                case documentation, and audit logging. Accountability and responsible data
                governance guide how the platform connects systems and supports investigations.
              </p>
            </div>
          </div>
        </section>

        <section className="section contact-section" id="contact">
          <div className="container contact-grid">
            <div>
              <div className="section-label">Contact / Demo</div>
              <h2>See how OdinEye fits your deployment.</h2>
              <p>
                Contact us to discuss LPR hosting, integrations, agency-controlled interoperability,
                mobile alerts, or a guided demonstration of the platform.
              </p>
            </div>
            <div className="contact-card">
              <span>Email</span>
              <a href="mailto:support@odineyelpr.com">support@odineyelpr.com</a>
              <span>Phone</span>
              <a href="tel:8603983575">860-398-3575</a>
              <a
                className="button button-solid"
                href="mailto:support@odineyelpr.com?subject=OdinEye%20Demo%20Request"
              >
                Request a Demo
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer-inner">
          <div>
            <strong>ODINEYE</strong>
            <span>A product of CellTrace LLC</span>
          </div>
          <div className="footer-links">
            <a href="#product">Product</a>
            <a href="#company">Company</a>
            <a href="mailto:support@odineyelpr.com">Contact</a>
            <a href="https://www.odineyelpr.com">Customer Login</a>
          </div>
          <div className="copyright">© {new Date().getFullYear()} CellTrace LLC</div>
        </div>
      </footer>
    </div>
  );
}

export default App;
